import "../assets/styles/components/hg-chatbot.scss";
import React, { useRef, useEffect, useState } from "react";
import sendIcon from "../assets/images/icon/send-icon.png";
import { Formik, Form, Field } from "formik";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useChatBot from "../hooks/useChatBot";
import { Message } from "../types/objectTypes";

interface HgChatbotProps {
  onGoBackClick: () => void;
}

const HgChatbot: React.FC<HgChatbotProps> = ({ onGoBackClick }) => {
  const { messages, send, isLoading, resumeSend } = useChatBot("program");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [showEmailButton, setShowEmailButton] = useState(false);

  const toggleEmailButton = (key: string) => {
    setShowEmailButton(key === "survey.end");
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    resumeSend(toggleEmailButton);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="hg-chatbot-container">
      <div className="go-back-btn">
        <button onClick={onGoBackClick}>
          Go back <i className="fa-solid fa-angle-up"></i>
        </button>
      </div>
      <h1>Discover the best homeschool program for you</h1>
      <p className="description">
        Find the best homeschool program for your child with personalized
        recommendations tailored to their learning needs.
      </p>
      <div className="hg-chatbot">
        <div className="chatbot-messages">
          {messages.map((message: Message, index: number) => {
            const showThinking =
              isLoading &&
              ((message.role === "assistant" && !message.content) ||
                message.role === "error");

            return (
              <div key={index} className={`message ${message.role}`}>
                {showThinking ? (
                  <div className="thinking">
                    <i className="fa-solid fa-spinner fa-spin"></i> Thinking...
                  </div>
                ) : message.content ? (
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </Markdown>
                ) : null}
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        <Formik
          initialValues={{ input: "", type: "program" }}
          validate={(values) => {
            const errors: any = {};
            if (!values.input.trim()) {
              errors.input = "Message cannot be empty";
            }
            return errors;
          }}
          onSubmit={(values, { resetForm }) => {
            resetForm();
            setShowEmailButton(false);
            send(values, toggleEmailButton);
          }}
        >
          {({ values }) => (
            <Form className="chatbot-form">
              <div className="input-field">
                <Field
                  type="text"
                  name="input"
                  placeholder="Write a message"
                  className="chatbot-input"
                  disabled={
                    isLoading || messages[messages.length - 1]?.role === "error"
                  }
                />
                {values.input.trim() && (
                  <button
                    type="submit"
                    className="chatbot-submit"
                    disabled={
                      isLoading ||
                      messages[messages.length - 1]?.role === "error"
                    }
                  >
                    <img src={sendIcon} alt="Send Icon" />
                  </button>
                )}
              </div>
            </Form>
          )}
        </Formik>

        <div className="buttons">
          <a
            href="https://inquiry.homeschoolglobal.com/modules/form-sign-up.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="move-forward-button" disabled={!showEmailButton}>
              Let’s move forward
            </button>
          </a>
          {showEmailButton && (
            <p className="button-description">
              Whether it’s your first step or your next, we’re here to make
              homeschooling simple, doable, and enjoyable.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HgChatbot;
